// services/TokenManager.js
import axios from 'axios';
import moment from 'moment';
import { Environment } from '@config';
import { AuthService, PreviousRouteService, SnackbarService } from '@services';

let refreshPromise = null;

const LEGACY_CREDENTIAL_KEYS = {
  keepSession: 'ls.credentials',
  manual: 'creds'
};

export const getAuthData = () => {
  try { return JSON.parse(localStorage.getItem('ls.authorizationData')) || null; }
  catch (e) { return null; }
};

const setStoredCredentials = (storageKey, userName, password) => {
  if (!storageKey || !userName || !password) {
    return;
  }

  localStorage.setItem(storageKey, JSON.stringify({
    userName,
    password
  }));
};

const syncLegacyCredentialsFromAuth = (data, fallbackUserName = '') => {
  if (!data?.pwd) {
    return;
  }

  const manualCreds = getLegacyCredentials('manual');
  const keepSessionCreds = getLegacyCredentials('keepSession');
  const userName = data.userName || fallbackUserName || manualCreds?.userName || keepSessionCreds?.userName;

  setStoredCredentials(LEGACY_CREDENTIAL_KEYS.manual, manualCreds?.userName || userName, data.pwd);

  if (keepSessionCreds?.userName) {
    setStoredCredentials(LEGACY_CREDENTIAL_KEYS.keepSession, keepSessionCreds.userName, data.pwd);
  }
};

export const setAuthData = (data, fallbackUserName = '') => {
  const currentAuthData = getAuthData();
  const userName = data.userName || fallbackUserName || currentAuthData?.userName || '';

  localStorage.setItem('ls.authorizationData', JSON.stringify({
    token: data.access_token,
    refreshToken: data.refresh_token,
    userName,
    useRefreshTokens: true,
    expirationDate: moment().add(data.expires_in, 'seconds')
  }));

  syncLegacyCredentialsFromAuth(data, userName);

  try { bc?.postMessage({ type: 'auth:updated' }); } catch (e) { /* BroadcastChannel no soportado */ }
  window.dispatchEvent(new CustomEvent('auth:updated'));
};

export const isTokenExpired = (skewSeconds = 15) => {
  const a = getAuthData();
  if (!a?.expirationDate) return true;
  const now = new Date();
  const exp = new Date(a.expirationDate);
  return now >= new Date(exp.getTime() - skewSeconds * 1000);
};

const getLegacyCredentials = (credentialScope = 'keepSession') => {
  const storageKey = LEGACY_CREDENTIAL_KEYS[credentialScope];

  if (!storageKey) {
    return null;
  }

  try {
    return JSON.parse(localStorage.getItem(storageKey)) || null;
  } catch (error) {
    return null;
  }
};

const expireSession = (error) => {
  PreviousRouteService.saveCurrentRoute();
  SnackbarService.error('Tu sesión ha expirado.');
  AuthService.clearStorage();
  AuthService.logout();
  throw error;
};

const doRefreshWithRefreshToken = async () => {
  const env = Environment.getCurrent();
  const a = getAuthData();
  const language = JSON.parse(localStorage.getItem('ls.ty-Language'))?.language;

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept-Language': language };
  const body = `grant_type=refresh_token&refresh_token=${encodeURIComponent(a.refreshToken)}&client_id=${env.clientId}`;

  const { data } = await axios.post(env.endpoints.auth, body, { headers });
  setAuthData(data, a?.userName);
  return data.access_token;
};

const doLoginWithCredentials = async (credentialScope = 'keepSession') => {
  const env = Environment.getCurrent();
  const creds = getLegacyCredentials(credentialScope);

  if (!creds?.userName || !creds?.password) {
    throw new Error('NO_CREDENTIALS');
  }

  const language = JSON.parse(localStorage.getItem('ls.ty-Language'))?.language;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept-Language': language };
  const body = `grant_type=password&username=${encodeURIComponent(creds.userName)}&password=${encodeURIComponent(creds.password)}&client_id=${env.clientId}&keep_session=true`;

  const { data } = await axios.post(env.endpoints.auth, body, { headers });
  setAuthData(data, creds.userName);
  return data.access_token;
};

export const refreshToken = async (options = {}) => {
  const {
    allowLegacyLogin = false,
    credentialScope = 'keepSession'
  } = options;

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      return await doRefreshWithRefreshToken();
    } catch (e1) {
      if (!allowLegacyLogin) {
        return expireSession(e1);
      }

      try {
        return await doLoginWithCredentials(credentialScope);
      } catch (e2) {
        return expireSession(e2);
      }
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Broadcast inter-pestañas
let bc = null;
try { bc = new BroadcastChannel('session'); } catch (e) { /* BroadcastChannel no soportado */ }

export const onAuthUpdated = (cb) => {
  const handler = () => cb();
  window.addEventListener('auth:updated', handler);
  bc?.addEventListener('message', (e) => { if (e.data?.type === 'auth:updated') cb(); });
  return () => window.removeEventListener('auth:updated', handler);
};
